import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Grid, Input, InputNumber, Modal, Pagination, Space, Table, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { catalogBrowse } from '../api';
import type { CatalogSearchResult } from '../api';
import ItemThumb from './ItemThumb';
import { useItemPictures } from '../hooks/useItemPictures';

const { Text } = Typography;

export interface PickedCatalogItem {
  item: CatalogSearchResult;
  qty: number;
  /** Line number this pick will get on the request (existing lines + picks so far). */
  lineNo?: number;
}

/** A line already on the request, so the browser can number new lines and flag repeats. */
export interface ExistingRequestLine {
  lineNo: number;
  catalogItemId: number | null;
  partNumber: string;
  description: string;
  qty: number;
  unit?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with everything added in this session when the user presses Done. */
  onDone: (picked: PickedCatalogItem[]) => void;
  /** Quantities already on the request (single-pick mode uses it as the default qty). */
  initial?: Record<number, number>;
  /** Lines already on the request: Add appends after them and says "also on line n" for repeats. */
  existingLines?: ExistingRequestLine[];
  showPrice?: boolean;
  /** Single pick: a "Select" button per row returns that item immediately (qty = initial or 1). */
  pickOne?: boolean;
  title?: string;
}

const CLASSES: Array<{ code: string; label: string }> = [
  { code: '', label: 'All' },
  { code: 'F', label: 'Fabrics' },
  { code: 'B', label: 'Beads & sequins' },
  { code: 'A', label: 'Trims & accessories' },
  { code: 'SRV', label: 'Services' },
  { code: 'CON', label: 'Consumables' },
  { code: 'MAT', label: 'Materials' },
  { code: 'EQP', label: 'Equipment' },
  { code: 'TOL', label: 'Tools' },
];

const PAGE_SIZE = 20;

/**
 * Full-size catalog browser used by the requester MR screen ("Browse catalog") and by
 * procurement's line editor. Search ignores Arabic letter shapes, digits, case, spaces and
 * hyphens (server-side cat_norm). Requesters never see prices.
 */
const CatalogBrowserModal: React.FC<Props> = ({ open, onClose, onDone, initial, existingLines = [], showPrice = false, pickOne = false, title }) => {
  const [q, setQ] = useState('');
  const [cls, setCls] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<CatalogSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // 2026-09-16: Add always adds a line (Ahmed's design). `picks` are the lines added this time, in order.
  const [picks, setPicks] = useState<PickedCatalogItem[]>([]);
  const [lastAdded, setLastAdded] = useState<Record<number, { lineNo: number; also: number[] }>>({});
  const [draftQty, setDraftQty] = useState<Record<number, number | null>>({});
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Phone width (2026-09-30): the fixed columns left Description 0px wide and every row ~1,600px tall.
  // Below md the modal takes the screen, the wide columns shrink and the table scrolls sideways.
  const narrow = !Grid.useBreakpoint().md;

  // Reset per opening; seed the cart with what is already on the request.
  useEffect(() => {
    if (!open) return;
    setQ(''); setCls(''); setPage(1); setPicks([]); setLastAdded({}); setDraftQty(pickOne && initial ? { ...initial } : {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async (query: string, classCode: string, p: number) => {
    setLoading(true);
    try {
      const r = await catalogBrowse({ q: query.trim() || undefined, class: classCode || undefined, page: p, pageSize: PAGE_SIZE });
      setRows(r.items); setTotal(r.total);
    } catch {
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(q, cls, page), 250);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [open, q, cls, page, load]);

  const add = (item: CatalogSearchResult) => {
    const n = Number(draftQty[item.id]);
    if (!(n > 0)) return;
    const lineNo = existingLines.length + picks.length + 1;
    const also = [
      ...existingLines.filter((l) => l.catalogItemId === item.id).map((l) => l.lineNo),
      ...picks.filter((p) => p.item.id === item.id).map((p) => p.lineNo!),
    ];
    setPicks((ps) => [...ps, { item, qty: n, lineNo }]);
    setLastAdded((m) => ({ ...m, [item.id]: { lineNo, also } }));
    setDraftQty((d) => ({ ...d, [item.id]: null }));
  };

  const removePick = (lineNo: number) => {
    setPicks((ps) => ps.filter((p) => p.lineNo !== lineNo).map((p, i) => ({ ...p, lineNo: existingLines.length + i + 1 })));
    setLastAdded({});
  };

  const cartList = picks;
  const requestLines = useMemo(() => [
    ...existingLines.map((l) => ({ lineNo: l.lineNo, code: l.partNumber, desc: l.description, qty: l.qty, unit: l.unit ?? '', isNew: false })),
    ...picks.map((p) => ({ lineNo: p.lineNo!, code: p.item.partNumber, desc: p.item.description, qty: p.qty, unit: p.item.unit?.name ?? '', isNew: true })),
  ], [existingLines, picks]);

  // Composer pictures for the rows on screen (2026-09-22): a thumbnail beside every suggestion, placeholder when none.
  const { pictures, reload: reloadPicture } = useItemPictures(rows.map((r) => r.id));

  const columns = [
    {
      title: 'Photo', key: 'photo', width: 64,
      render: (_: any, r: CatalogSearchResult) => (
        <ItemThumb size="sm" url={pictures[r.id]?.url ?? null} itemId={r.id} code={r.partNumber} description={r.description} descriptionAr={r.descriptionAr} bytes={pictures[r.id]?.size ?? null} onError={() => reloadPicture(r.id)} />
      ),
    },
    {
      title: 'Code', dataIndex: 'partNumber', width: narrow ? 150 : 250,
      render: (v: string) => <Tag style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600, ...(narrow ? { whiteSpace: 'normal', wordBreak: 'break-all' } : {}) }}>{v}</Tag>,
    },
    {
      title: 'Description', key: 'desc', ...(narrow ? { width: 220 } : {}),
      render: (_: any, r: CatalogSearchResult) => (
        <div style={{ lineHeight: 1.35 }}>
          <div>{r.description}</div>
          {r.descriptionAr && <div dir="rtl" style={{ color: '#666', textAlign: 'left' }}>{r.descriptionAr}</div>}
        </div>
      ),
    },
    { title: 'Unit', key: 'unit', width: 80, render: (_: any, r: CatalogSearchResult) => r.unit?.name ?? <Text type="warning">none</Text> },
    { title: 'Preferred vendor', key: 'vendor', width: narrow ? 120 : 160, render: (_: any, r: CatalogSearchResult) => r.preferredVendor?.name ?? '—' },
    ...(showPrice ? [{
      title: 'Default price', key: 'price', width: 110, align: 'right' as const,
      render: (_: any, r: CatalogSearchResult) => r.defaultUnitPrice != null ? `${Number(r.defaultUnitPrice).toLocaleString(undefined, { minimumFractionDigits: 2 })}${r.currency?.code ? ' ' + r.currency.code : ''}` : '—',
    }] : []),
    {
      title: pickOne ? '' : 'Quantity', key: 'qty', width: pickOne ? 100 : 190, align: 'right' as const,
      render: (_: any, r: CatalogSearchResult) => {
        if (pickOne) return <Button size="small" type="primary" onClick={() => onDone([{ item: r, qty: Number(initial && Object.values(initial)[0]) || 1 }])}>Select</Button>;
        const la = lastAdded[r.id];
        return (
          <div style={{ textAlign: 'right' }}>
            <Space>
              <InputNumber
                size="small" min={0} step={1} style={{ width: 84 }}
                value={draftQty[r.id] ?? null}
                onChange={(v) => setDraftQty((d) => ({ ...d, [r.id]: v === null ? null : Number(v) }))}
                onPressEnter={() => add(r)}
                placeholder="0"
              />
              <Button size="small" type="primary" onClick={() => add(r)}>Add</Button>
            </Space>
            {la && (
              <div style={{ fontSize: 11.5, color: '#1a7f4b', marginTop: 3, whiteSpace: 'nowrap' }}>
                ✓ Added to line {la.lineNo}{la.also.length ? ` · also on line${la.also.length > 1 ? 's' : ''} ${la.also.join(', ')}` : ''}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={narrow ? 'calc(100vw - 16px)' : 1100}
      style={narrow ? { top: 8, maxWidth: 'none', paddingBottom: 0 } : undefined}
      title={<Space>{title || 'Browse catalog'} <Tag>{total} items</Tag></Space>}
      destroyOnClose
      footer={pickOne ? <Button onClick={onClose}>Close</Button> : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Text type="secondary">Can't find it? Close and use "Not in catalog".</Text>
          <Space>
            <Button onClick={onClose}>Close</Button>
            <Button type="primary" disabled={cartList.length === 0} onClick={() => onDone(cartList)}>
              Done{cartList.length ? ` (${cartList.length} new line${cartList.length === 1 ? '' : 's'})` : ''}, back to request
            </Button>
          </Space>
        </div>
      )}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Input
          autoFocus allowClear size="large" prefix={<SearchOutlined />}
          placeholder="Search code, English or Arabic description"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
        />
        <Space wrap>
          {CLASSES.map((c) => (
            <Tag.CheckableTag key={c.code} checked={cls === c.code} onChange={() => { setCls(c.code); setPage(1); }}>{c.label}</Tag.CheckableTag>
          ))}
        </Space>
        <Table
          size="small" rowKey="id" loading={loading} columns={columns} dataSource={rows} pagination={false}
          scroll={narrow ? { x: columns.reduce((w, c) => w + (Number(c.width) || 0), 0), y: 380 } : { y: 380 }}
          locale={{ emptyText: q ? 'No items match. Try fewer letters, or close and use "Not in catalog".' : 'Type to search, or pick a class.' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination size="small" current={page} pageSize={PAGE_SIZE} total={total} showSizeChanger={false} onChange={setPage} />
        </div>
        {!pickOne && (requestLines.length === 0 ? (
          <Alert type="info" showIcon message="Nothing on this request yet" description="Type a quantity on a row and press Add. Press Add again for the same item to add another line (for example for another cost center)." />
        ) : (
          <div style={{ border: '1px solid #e3e6ec', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '6px 10px', fontSize: 11.5, letterSpacing: '.04em', textTransform: 'uppercase', color: '#8a94a6', background: '#f8f9fb', borderBottom: '1px solid #e3e6ec' }}>
              Lines on this request · {requestLines.length}
            </div>
            <div style={{ maxHeight: 150, overflowY: 'auto' }}>
              {requestLines.map((l) => (
                <div key={l.lineNo} style={{ display: 'grid', gridTemplateColumns: '34px 1fr 110px 28px', gap: 10, alignItems: 'center', padding: '5px 10px', borderBottom: '1px solid #f0f1f4', fontSize: 13, background: l.isNew ? '#e8f0fd' : undefined }}>
                  <span style={{ color: '#8a94a6', fontSize: 12 }}>{l.lineNo}</span>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.code ? <Tag style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600 }}>{l.code}</Tag> : <Tag color="orange">free text</Tag>}{l.desc}
                  </span>
                  <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.qty} {l.unit}</span>
                  {l.isNew ? <Button size="small" type="text" onClick={() => removePick(l.lineNo)} aria-label={`Remove line ${l.lineNo}`}>✕</Button> : <span />}
                </div>
              ))}
            </div>
          </div>
        ))}
      </Space>
    </Modal>
  );
};

export default CatalogBrowserModal;
